import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Grid,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect } from 'react';
import { IDTransactionLog, ITransactionLogMeta, TrxLogType, TransactionStatus } from 'realhaus-sdk';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StringUtils from '../../../utils/string';
import { RootStoreContext } from '../../../global/storeContext';
import { ConfirmDialog } from '../../uiComponent/confirmDialog';

export const TransactionDrawer: React.FC<{ transactionLog?: IDTransactionLog }> = observer(
    ({ transactionLog }) => {
        if (!transactionLog) return <></>;
        return (
            <div>
                <Grid container mt={12} spacing={3} mb={3}>
                    <Grid item xs={12} justifyContent='center' textAlign='center'>
                        <Typography variant='h6'>
                            <b>{transactionLog.type}</b>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Accordion defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls='transaction-summary'
                                id='transaction-summary'
                            >
                                <Typography>Transaction Summary</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TransactionSummary log={transactionLog} />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                        <Accordion defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls='panel2-content'
                                id='meta'
                            >
                                <Typography>Create Refund</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RefundInformation log={transactionLog} />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                        <Accordion defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls='panel2-content'
                                id='meta'
                            >
                                <Typography>Meta Information</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ maxHeight: '200px', overflow: 'auto' }}>
                                {transactionLog.meta && (
                                    <MetaInformation meta={transactionLog.meta} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </div>
        );
    },
);

const Title = ({ text }: { text: string }) => (
    <Typography variant='subtitle2' fontWeight='600'>
        {text}
    </Typography>
);

const TransactionSummary = ({ log }: { log: IDTransactionLog }) => {
    return (
        <Grid container>
            {log && (
                <>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Transaction ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.id}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Transaction Date' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {StringUtils.getDateTimeFormat(log.timestamp)}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Transaction Type' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.type}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Amount' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {StringUtils.asCurrency(log.amount)}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Sender ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.senderId}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Receiver ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.receiverId}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Reference ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.referenceId}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Narration' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.narration}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Status' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.status}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Source' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.source}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Status Message' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.statusMsg}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Notes' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{log.notes}</Typography>
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    );
};
const MetaInformation = ({ meta }: { meta: ITransactionLogMeta }) => {
    const renderValue = (value: any) => {
        if (typeof value === 'object' && value !== null) {
            return (
                <ul>
                    {Object.entries(value).map(([key, val]) => (
                        <li key={key}>
                            <strong>{key.replace('_', ' ')}:</strong> {renderValue(val)}
                        </li>
                    ))}
                </ul>
            );
        }
        return value?.toString() || '';
    };

    return (
        <Grid container>
            {meta &&
                Object.entries(meta).map((m, i) => (
                    <>
                        <Grid container item xs={12} key={i}>
                            <Grid item xs={4}>
                                <Title text={m[0].replace('_', ' ')} />
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant='body2'>{renderValue(m[1])}</Typography>
                            </Grid>
                        </Grid>
                    </>
                ))}
        </Grid>
    );
};
const RefundInformation = ({ log }: { log: IDTransactionLog }) => {
    const [disableRefund, setDisableRefund] = React.useState<boolean>(true);
    const { uiStore, dashboardStore } = useContext(RootStoreContext);
    const [relatedRefundTrxLogs, setRelatedRefundTrxLogs] = React.useState<IDTransactionLog[]>([]);
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false);

    useEffect(() => {
        refetchRefundTransactions();
    }, []);

    const refetchRefundTransactions = async () => {
        uiStore.showLoading();
        try {
            await dashboardStore.findPaymentRefundTransaction(log.id).then((trxLogs) => {
                if (!trxLogs) return;
                const isRefundable =
                    log.type === TrxLogType.PAYMENT &&
                    log.status === TransactionStatus.SUCCESS &&
                    !trxLogs.find(
                        (refundTrxLog) => refundTrxLog.status === TransactionStatus.SUCCESS,
                    );
                setRelatedRefundTrxLogs(trxLogs);
                setDisableRefund(!isRefundable);
            });
        } catch (err) {
            console.error(`Failed to find related refund transactions for this trxlogId ${log.id}`);
        } finally {
            uiStore.hideLoading();
        }
    };

    const handleRefund = async () => {
        uiStore.showLoading();
        try {
            await dashboardStore.refundTransaction(log.id).then(() => {
                uiStore.success('Refund successful');
                refetchRefundTransactions();
            });
        } catch (err) {
            uiStore.error('Failed to make refund');
            console.error(err);
        } finally {
            uiStore.hideLoading();
        }
    };

    return (
        <>
            <Grid container>
                <Grid
                    container
                    item
                    direction='row'
                    justifyContent='flex-end'
                    alignItems='center'
                    xs={12}
                >
                    <Grid item>
                        <Button
                            variant='outlined'
                            disabled={disableRefund}
                            onClick={() => setOpenConfirmDialog(true)}
                        >
                            Refund
                        </Button>
                    </Grid>
                </Grid>

                {relatedRefundTrxLogs.length < 1 ? (
                    <Grid item>
                        <Typography variant='body2'>
                            There is no related refund transaction log info for this transaction
                        </Typography>
                    </Grid>
                ) : (
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Refund TransactionLog ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Title text='Status' />
                        </Grid>
                        {relatedRefundTrxLogs.map((log) => (
                            <>
                                <Grid item xs={4}>
                                    <Typography variant='body2'>{log.id}</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant='body2'>{log.status}</Typography>
                                </Grid>
                            </>
                        ))}
                    </Grid>
                )}
            </Grid>
            <ConfirmDialog
                title={'Create Refund'}
                content={'Are you sure you want to make a refund for this transaction?'}
                openDialog={openConfirmDialog}
                closeDialog={(confirm: boolean) => () => {
                    if (confirm) {
                        handleRefund();
                    }
                    setOpenConfirmDialog(false);
                }}
            />
        </>
    );
};
